import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, LineChart, Skeleton, DayBars, LockVeil, type ChartSeries, type DayBarSeries } from '../../../../components';
import { HOME, HOME_SERIES_COLORS, HOME_NARROW } from '../../../../theme/tokens';
import { useAcquisitionChannelGate } from '../../../../hooks/useAcquisitionChannelGate';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import type { HomeChannel, HomePeriod } from '../../../../mock-data/types';
import { MetricFrame } from '../MetricFrame';
import { Legend } from '../Legend';
import { StatValue } from '../StatValue';
import { Delta } from '../Delta';
import {
  Channels,
  Card,
  Top,
  Lhs,
  IconSlot,
  Name,
  Chev,
  Body,
  Nums,
  ChStat,
  ChNum,
  ChLabel,
  ChDelta,
  ChCap,
  Chart,
  LegendWrap,
} from './AcquisitionChannels.styles';

// Mini-trend height. Stacked under the figures it's a flat sparkline; when the
// card goes full-width (figures left, chart to the right) it grows to roughly the
// figures' block height so the trend reads as tall as the numbers beside it.
const CHART_HEIGHT = 62;
const CHART_HEIGHT_WIDE = 96;

/** Compact count for the day-bar tooltip ("80", "1.2k"). */
const countFmt = (n: number): string => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${Math.round(n)}`);

type ChannelCardProps = {
  channel: HomeChannel;
  dates: string[];
  /** Per-sample hover headings (mirrors the page's interval); `[]` outside the hourly ranges. */
  pointLabels: string[];
  /** The selected range — Today/Yesterday swap the mini-trend for day-comparison bars. */
  interval: HomePeriod;
  /** Delta caption ("vs yesterday" / "vs last week" / …) — adapts to the range. */
  comparison: string;
  /** Stagger the two figures' count-up start. */
  delay: number;
  /** Grid-level loading fallback when a card carries no per-card `loading` flag. */
  loading: boolean;
  /** Drill into the channel's page — reads the channel id from `data-id`. */
  onOpen: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
};

const ChannelCard = ({ channel, dates, pointLabels, interval, comparison, delay, loading, onOpen }: ChannelCardProps): React.ReactElement => {
  const { t } = useTranslation();
  // Reactive gate: re-renders this card when a channel is unlocked/locked at runtime.
  const { isEnabled } = useAcquisitionChannelGate();
  // Cards resolve independently: prefer the card's own status, falling back to the
  // grid-level flag (the mock path passes the grid flag and no per-card status).
  const cardLoading = channel.loading ?? loading;
  const cardError = channel.error ?? false;
  // Below HOME_NARROW the card stacks (chart under the figures) and stays a flat
  // sparkline; on wide desktop it sits beside the figures and grows to match them.
  const stacked = useMediaQuery(HOME_NARROW);
  // Today/Yesterday have no usable hourly slices — show day-comparison bars instead.
  const dayView = interval === 'TODAY' || interval === 'YESTERDAY';
  const chartHeight = stacked ? CHART_HEIGHT : CHART_HEIGHT_WIDE;
  // Per the source design, the channel mini-charts reuse the financial palette
  // (not channel-specific colours): Registrations=ggr, FTDs=stakes, FTD Value=wins.
  const series: ChartSeries[] = [
    { name: t('Registrations'), color: HOME_SERIES_COLORS.ggr, points: channel.chart.registrations },
    { name: t('FTDs'), color: HOME_SERIES_COLORS.stakes, points: channel.chart.ftds },
    { name: t('FTD Value'), color: HOME_SERIES_COLORS.wins, points: channel.chart.ftdValue, dashed: true },
  ];
  const dayBars: DayBarSeries[] = [
    { name: t('Registrations'), color: HOME_SERIES_COLORS.ggr, ...channel.days.registrations },
    { name: t('FTDs'), color: HOME_SERIES_COLORS.stakes, ...channel.days.ftds },
    { name: t('FTD Value'), color: HOME_SERIES_COLORS.wins, dashed: true, ...channel.days.ftdValue },
  ];

  // Gated channels (release feature flag) are inert and frosted under a padlock instead of
  // drilling into their detail page. The self-managed cards (Affiliates / Influencers) gate
  // the same way, via their own registry. See `config/acquisitionChannels`.
  const locked = !isEnabled(channel.id);

  return (
    <Card
      data-id={channel.id}
      $locked={locked}
      role={locked ? undefined : 'button'}
      tabIndex={locked ? -1 : 0}
      aria-disabled={locked || undefined}
      aria-label={locked ? undefined : t('Open {{name}}', { name: channel.name })}
      onClick={locked ? undefined : onOpen}
      onKeyDown={
        locked
          ? undefined
          : (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen(event);
              }
            }
      }
    >
      <Top>
        <Lhs>
          <IconSlot>
            <Icon name={channel.icon} size={22} color={HOME.t50} />
          </IconSlot>
          <Name text={channel.name} tag="span" />
        </Lhs>
        <Chev className="chev" aria-hidden="true">
          <Icon name="arrow-right-circle" size={24} color={HOME.t50} />
        </Chev>
      </Top>
      <Body>
        <Nums>
          <ChStat>
            <ChNum>
              <StatValue value={channel.registrations} delay={delay} loading={cardLoading} error={cardError} />
            </ChNum>
            <ChLabel text="Registrations" tag="span" />
            {/* On error there is no real delta — drop the pill + caption. */}
            {!cardError ? (
              <ChDelta>
                {cardLoading ? <Skeleton width={44} height={18} radius={8} /> : <Delta value={channel.delta} />}
                <ChCap text={comparison} tag="span" />
              </ChDelta>
            ) : null}
          </ChStat>
          <ChStat>
            <ChNum>
              <StatValue value={channel.ftds} delay={delay + 60} loading={cardLoading} error={cardError} />
            </ChNum>
            <ChLabel text="First Time Depositors" tag="span" />
            {!cardError ? (
              <ChDelta>
                {cardLoading ? <Skeleton width={44} height={18} radius={8} /> : <Delta value={channel.delta} />}
                <ChCap text={comparison} tag="span" />
              </ChDelta>
            ) : null}
          </ChStat>
        </Nums>
        <Chart>
          {cardLoading ? (
            <Skeleton block height={chartHeight} radius={10} />
          ) : dayView ? (
            <DayBars series={dayBars} view={interval === 'TODAY' ? 'today' : 'yesterday'} height={chartHeight} compact formatValue={countFmt} />
          ) : (
            <LineChart series={series} xLabels={dates} pointLabels={pointLabels} height={chartHeight} showY={false} />
          )}
        </Chart>
      </Body>
      {/* Match the enabled card's header (Icon 22 · Name 18px · gap 16 · padding 12) so the
          veil's header overlays it exactly, with the padlock where the chevron sits (top-right). */}
      {locked ? (
        <LockVeil name={channel.name} icon={channel.icon} iconSize={22} fontSize={18} headerGap={16} pad={12} lockCorner="top-right" />
      ) : null}
    </Card>
  );
};

export type AcquisitionChannelsProps = {
  channels: HomeChannel[];
  dates: string[];
  /** Per-sample hover headings for the mini-charts (mirrors the interval); `[]` outside the hourly ranges. */
  pointLabels: string[];
  /** The selected range — Today/Yesterday swap the mini-trends for day-comparison bars. */
  interval: HomePeriod;
  /** Delta caption ("vs yesterday" / "vs last week" / …) — adapts to the range. */
  comparison: string;
  /** Grid-level loading fallback; cards may also carry their own `loading` flag. */
  loading?: boolean;
  /** Drill into a channel's page — reads the channel id from the card's `data-id`. */
  onOpenChannel: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
};

/**
 * "Acquisition Channels" — a grid of per-channel cards (icon, name, two figures
 * with deltas and a three-line mini trend), plus a shared legend underneath.
 * Each card drills into its respective channel page.
 */
export const AcquisitionChannels = ({ channels, dates, pointLabels, interval, comparison, loading = false, onOpenChannel }: AcquisitionChannelsProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <MetricFrame title={t('Acquisition Channels')}>
      <Channels>
        {channels.map((c, index) => (
          <ChannelCard key={c.id} channel={c} dates={dates} pointLabels={pointLabels} interval={interval} comparison={comparison} delay={index * 60} loading={loading} onOpen={onOpenChannel} />
        ))}
      </Channels>
      <LegendWrap>
        <Legend
          items={[
            { name: t('Registrations'), color: HOME_SERIES_COLORS.ggr },
            { name: t('FTDs'), color: HOME_SERIES_COLORS.stakes },
            { name: t('FTD Value'), color: HOME_SERIES_COLORS.wins, dashed: true },
          ]}
        />
      </LegendWrap>
    </MetricFrame>
  );
};

export default AcquisitionChannels;
